/**
 * Upload service for file management.
 *
 * Handles file uploads to the backend and provides methods for
 * listing, deleting, and getting signed URLs for files.
 */

import axios from 'axios';
import {
  MultiFileUploadResponse,
  FileListResponse,
  SignedUrlResponse,
  UploadedFileResponse,
} from '../../types/upload';
import { getAuthProvider } from '../auth/authProviderFactory';

// Use VITE_API_BASE_URL for production - it points to the API domain
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/**
 * Progress callback type for upload tracking.
 */
export type UploadProgressCallback = (
  fileIndex: number,
  progress: number
) => void;

/**
 * Upload one or more files for a project.
 *
 * @param projectId - The project ID to associate files with
 * @param files - Array of File objects to upload
 * @param onProgress - Optional callback for tracking upload progress
 * @returns Promise resolving to the upload response
 */
export async function uploadFiles(
  projectId: string,
  files: File[],
  onProgress?: UploadProgressCallback
): Promise<MultiFileUploadResponse> {
  const formData = new FormData();

  // Append all files to the form data
  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await axios.post<MultiFileUploadResponse>(
    `${API_BASE}/projects/${projectId}/files`,
    formData,
    {
      // task_111: send the gml_sso cookie cross-subdomain on the Studio path.
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          // Calculate overall progress (we can't track individual files with axios)
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          // Report progress for all files at once
          files.forEach((_, index) => {
            onProgress(index, progress);
          });
        }
      },
    }
  );

  return response.data;
}

/**
 * Upload a single file with progress tracking.
 * Uses XMLHttpRequest for more granular progress control.
 *
 * @param projectId - The project ID
 * @param file - The file to upload
 * @param onProgress - Progress callback (0-100)
 * @returns Promise resolving to the uploaded file response
 */
export async function uploadSingleFile(
  projectId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<UploadedFileResponse> {
  // task_111: provider-aware. Firebase returns a Bearer token; Studio returns
  // null (the gml_sso cookie carries auth via xhr.withCredentials below).
  const token = await getAuthProvider().getToken();

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('files', file);

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded * 100) / event.total);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response: MultiFileUploadResponse = JSON.parse(
            xhr.responseText
          );
          if (response.files && response.files.length > 0) {
            resolve(response.files[0]);
          } else {
            reject(new Error('No file in response'));
          }
        } catch {
          reject(new Error('Failed to parse response'));
        }
      } else {
        try {
          const errorResponse = xhr.responseText;
          console.error('[UPLOAD] Server error response:', errorResponse);
          const error = JSON.parse(errorResponse);
          reject(
            new Error(
              error.detail ||
                error.error?.message ||
                `Upload failed: ${xhr.status}`
            )
          );
        } catch {
          console.error('[UPLOAD] Raw error response:', xhr.responseText);
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('POST', `${API_BASE}/projects/${projectId}/files`);
    xhr.withCredentials = true;
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send(formData);
  });
}

/**
 * List all files for a project.
 *
 * @param projectId - The project ID
 * @returns Promise resolving to the file list
 */
export async function listFiles(projectId: string): Promise<FileListResponse> {
  const response = await axios.get<FileListResponse>(
    `${API_BASE}/projects/${projectId}/files`,
    { withCredentials: true }
  );
  return response.data;
}

/**
 * Delete a file from a project.
 *
 * @param projectId - The project ID
 * @param fileId - The file ID to delete
 * @returns Promise resolving when deletion is complete
 */
export async function deleteFile(
  projectId: string,
  fileId: string
): Promise<void> {
  await axios.delete(`${API_BASE}/projects/${projectId}/files/${fileId}`, {
    withCredentials: true,
  });
}

/**
 * Get a signed URL for downloading a file.
 *
 * @param projectId - The project ID
 * @param fileId - The file ID
 * @param expiresIn - Optional expiration time in minutes (default: 60)
 * @returns Promise resolving to the signed URL response
 */
export async function getSignedUrl(
  projectId: string,
  fileId: string,
  expiresIn: number = 60
): Promise<SignedUrlResponse> {
  const response = await axios.get<SignedUrlResponse>(
    `${API_BASE}/projects/${projectId}/files/${fileId}/url`,
    {
      withCredentials: true,
      params: { expires_in: expiresIn },
    }
  );
  return response.data;
}

/**
 * Upload service singleton for convenience.
 */
export const uploadService = {
  uploadFiles,
  uploadSingleFile,
  listFiles,
  deleteFile,
  getSignedUrl,
};

export default uploadService;
